import {
  takeEvery,
  CallEffect,
  delay,
  call,
  put,
  PutEffect,
  SelectEffect,
  select,
  take,
  TakeEffect,
  all
} from 'redux-saga/effects';

import {
  GENERATE_IMAGES,
  imageGenerationSuccess,
  imageGenerationFailed,
  REGENERATE_IMAGES,
  ADD_GENERATED_IMAGE_TO_PROJECT,
  AddGeneratedImageToProjectAction,
  TRY_NEW_IMAGE_PROMPT,
  resetImageGeneration,
  changeAiPanelStep,
  generateScriptSuccess,
  generateScriptFailed,
  GENERATE_SCRIPT,
  SAVE_GENERATED_SCRIPT,
  TryNewScriptPromptAction,
  TRY_NEW_SCRIPT_PROMPT,
  resetScriptGeneration,
  SaveGeneratedScriptAction,
  ChangeStepTrigger,
  GenerateScriptAction
} from 'js/actionCreators/aiFeaturesActions';
import { showError, showLeftHandPanel } from 'js/actionCreators/uiActions';
import { appServices } from 'js/shared/helpers/app-services/AppServices';
import { FILE_CONTENT_TYPES, IMAGE_GEN_STYLES, IMAGE_PROVIDER_TYPES, LIMITS } from 'js/config/consts';
import {
  RootState,
  ImageGenerationInputs,
  ImageGenerationProgressApiData,
  ImageGenerationStatus,
  ImageGenerationApiData,
  VoiceoverGenerationApiData,
  VoiceoverGenerationProgressApiData,
  VoiceoverGenerationStatus,
  VSCAssetImageSourceName,
  AudioSource,
  InputVoiceData,
  AiPanelTab,
  AiImageGenerationTabStep,
  ScriptGenerationInputs,
  AiScriptGenerationTabStep,
  GeneratedScene,
  ScriptGenerationApiJobCreationData,
  ScriptGenerationApiStatusData,
  ScriptGenerationStatus,
  WordBoundary,
  AzureWordBoundary,
  LeftHandPanel
} from 'js/types';
import {
  PROCESS_AI_IMAGES_FAILURE,
  PROCESS_AI_IMAGES_SUCCESS,
  ProcessAiImagesFailureAction,
  processAiImagesRequest,
  ProcessAiImagesSuccessAction
} from 'js/actionCreators/imageProcessingActions';
import { addUserImageToScribe } from 'js/actionCreators/imagesActions';
import {
  saveAudioToLibrary,
  SAVE_AUDIO_TO_LIBRARY_FAILED,
  SAVE_AUDIO_TO_LIBRARY_SUCCESS,
  SaveAudioToLibrarySuccessAction,
  SaveAudioToLibraryFailedAction
} from 'js/actionCreators/audioActions';
import { ALLOWED_AUDIO_MIME_TYPE, AI_FEATURES } from 'js/config/config';
import {
  urlToFileRequest,
  URL_TO_FILE_SUCCESS,
  URL_TO_FILE_FAILURE,
  UrlToFileSuccessAction,
  UrlToFileFailureAction
} from 'js/actionCreators/urlToFileActions';
import { decrementLimit } from 'js/actionCreators/limitActions';
import { setEditingScript, syncSuccess, updateEditingScript } from 'js/reducers/projectScripts';
import {
  generateVoiceover,
  voiceoverGenerationFailed,
  voiceoverGenerationSuccess,
  VoiceoversState
} from 'js/reducers/voiceoversReducer';

import { mapImageGenerationFailedReasonToMessage } from './sagaHelpers/mapImageGenerationFailedReasonToMessage';
import { getViewportData } from './sagaHelpers/getViewportData';
import { uploadImageAssetWithRetries } from './imageUploadSagas';

export const getImageGenerationInputs = (state: RootState) => state.aiFeatures.imageGenerationInputs;
export const getImageGenerationResults = (state: RootState) => state.aiFeatures.imageGenerationResults;
export const getVoiceoverGenerationResults = (state: RootState) => state.voiceovers.voiceoverGenerationResults;
export const getScriptGenerationInputs = (state: RootState) => state.aiFeatures.scriptGenerationInputs;

type UploadImageAssetWithRetriesReturnType =
  | { assetId: number; signedImgUrl: string; error: undefined }
  | { assetId: undefined; signedImgUrl: undefined; error: Error };

const DELAY_MS = 1000;
const MAX_RETRIES = 3;
const IMAGE_FILENAME = 'AI Generated Image';
const ERROR_MESSAGE = 'Failed to generate image';

function* handleImageGenerationProgress(executionId: string) {
  let isDone = false;

  while (!isDone) {
    const response = (yield call(
      appServices.fetchImageGenerationProgress,
      executionId
    )) as ImageGenerationProgressApiData;

    if (response.status === ImageGenerationStatus.IN_PROGRESS) {
      yield delay(DELAY_MS);
      continue;
    }

    isDone = true;

    if (response.status === ImageGenerationStatus.COMPLETE) {
      return response;
    }

    const failedMessage = mapImageGenerationFailedReasonToMessage(response.failedReason);
    throw new Error(failedMessage);
  }
}

function* handleImageProcessing(response: ImageGenerationProgressApiData, executionId: string) {
  if (response.status !== ImageGenerationStatus.COMPLETE) return;

  const images = response.signedUrls.map(url => ({ url }));
  yield put(processAiImagesRequest({ images }));

  const processedImagesResult = (yield take([PROCESS_AI_IMAGES_SUCCESS, PROCESS_AI_IMAGES_FAILURE])) as
    | ProcessAiImagesSuccessAction
    | ProcessAiImagesFailureAction;

  if ('error' in processedImagesResult) throw processedImagesResult.error;

  const uploadPromises = processedImagesResult.images.map(image =>
    call(uploadImageAssetWithRetries, {
      file: image.blob,
      filename: IMAGE_FILENAME,
      source: VSCAssetImageSourceName.AI,
      maxRetries: MAX_RETRIES,
      sourceIdentifier: executionId
    })
  );

  const uploadResults = (yield all(uploadPromises)) as UploadImageAssetWithRetriesReturnType[];

  if (uploadResults.some(result => result.error)) throw new Error('Failed to upload image assets');

  const validUploadResults = uploadResults.filter(
    (result): result is { assetId: number; signedImgUrl: string; error: undefined } =>
      result.assetId !== undefined && result.signedImgUrl !== undefined
  );

  const uploadedAssets = validUploadResults.map(result => ({
    assetId: result.assetId,
    url: result.signedImgUrl
  }));

  yield put(decrementLimit(LIMITS.IMAGE_GENERATIONS, 1));
  yield put(imageGenerationSuccess(uploadedAssets, response.id, response.metadata.enhancedPrompt));
}

function* generateImages(): Generator<
  CallEffect | PutEffect | SelectEffect,
  void,
  ImageGenerationApiData | ImageGenerationProgressApiData | ImageGenerationInputs
> {
  const { style, prompt } = (yield select(getImageGenerationInputs)) as ImageGenerationInputs;
  const model = IMAGE_GEN_STYLES[style];

  try {
    const { executionId } = (yield call(
      appServices.generateImages,
      model,
      prompt,
      AI_FEATURES.IMAGE.GENERATION_QUANTITY
    )) as ImageGenerationApiData;
    const response = (yield call(handleImageGenerationProgress, executionId)) as ImageGenerationProgressApiData;
    yield call(handleImageProcessing, response, executionId);
  } catch (error) {
    if (error instanceof Error) {
      yield put(imageGenerationFailed(`${ERROR_MESSAGE}: ${error.message}`));
      yield put(showError({ message: ERROR_MESSAGE, description: error.message }));
      yield put(changeAiPanelStep(AiPanelTab.IMAGE_GENERATION, AiImageGenerationTabStep.OVERVIEW));
    }
  }
}

function* regenerateImages(): Generator<SelectEffect | PutEffect, void, ImageGenerationInputs> {
  const { style, prompt } = yield select(getImageGenerationInputs);
  yield put({
    type: GENERATE_IMAGES,
    style: style,
    prompt: prompt
  });
}

function* addGeneratedImageToProject({
  assetId,
  project,
  rating,
  eventTrigger
}: AddGeneratedImageToProjectAction): Generator<CallEffect | PutEffect, void, Blob> {
  yield put(
    addUserImageToScribe(
      assetId,
      FILE_CONTENT_TYPES.SVG,
      project.id,
      getViewportData(project.canvasSize),
      { title: 'AI Image Generator Panel' },
      'AI Generated Image',
      IMAGE_PROVIDER_TYPES.AI_LIBRARY,
      rating,
      eventTrigger,
      VSCAssetImageSourceName.AI
    )
  );
}

function* tryNewImagePrompt(): Generator<PutEffect, void, void> {
  yield put(resetImageGeneration());
  yield put(changeAiPanelStep(AiPanelTab.IMAGE_GENERATION, AiImageGenerationTabStep.OVERVIEW));
}

function* callGenerateVoiceover(
  prompt: string,
  locale: string,
  voice: InputVoiceData
): Generator<CallEffect, VoiceoverGenerationApiData, VoiceoverGenerationApiData> {
  return yield call(appServices.generateVoiceover, prompt, locale, voice.gender, voice.originalName);
}

function* callFetchVoiceoverProgress(
  executionId: string
): Generator<CallEffect, VoiceoverGenerationProgressApiData, VoiceoverGenerationProgressApiData> {
  return yield call(appServices.fetchVoiceoverGenerationProgress, executionId);
}

async function fetchWordBoundaryData(url: string) {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Failed to fetch word boundary data: ${response.statusText}`);
  }
  return response.json();
}

function* handleVoiceoverGenerationSuccess(
  response: VoiceoverGenerationProgressApiData,
  prompt: string,
  scriptName?: string
): Generator<
  CallEffect | PutEffect | TakeEffect,
  void,
  | SaveAudioToLibrarySuccessAction
  | SaveAudioToLibraryFailedAction
  | UrlToFileSuccessAction
  | UrlToFileFailureAction
  | AzureWordBoundary[]
> {
  if (response.status !== VoiceoverGenerationStatus.SUCCEEDED) return;

  yield put(urlToFileRequest({ url: response.url, filename: scriptName || prompt, mimeType: ALLOWED_AUDIO_MIME_TYPE }));
  const processResourceResult = (yield take([URL_TO_FILE_SUCCESS, URL_TO_FILE_FAILURE])) as
    | UrlToFileSuccessAction
    | UrlToFileFailureAction;

  if ('error' in processResourceResult) throw processResourceResult.error;

  let wordBoundaryData: WordBoundary[] | undefined = undefined;
  if (response.wordBoundaryUrl) {
    const boundaryData = (yield call(fetchWordBoundaryData, response.wordBoundaryUrl)) as AzureWordBoundary[];
    if (Array.isArray(boundaryData)) {
      wordBoundaryData = boundaryData.map(boundary => ({
        text: boundary.Text,
        audioOffsetMs: boundary.AudioOffset,
        durationMs: boundary.Duration
      }));
    }
  }

  yield put(saveAudioToLibrary(processResourceResult.file, AudioSource.AI_GENERATED, wordBoundaryData));

  const saveVoiceoverResult = (yield take([SAVE_AUDIO_TO_LIBRARY_FAILED, SAVE_AUDIO_TO_LIBRARY_SUCCESS])) as
    | SaveAudioToLibrarySuccessAction
    | SaveAudioToLibraryFailedAction;

  if ('error' in saveVoiceoverResult) throw saveVoiceoverResult.error;
  const audioClipData = saveVoiceoverResult.audioClipData;

  yield put(decrementLimit(LIMITS.VOICEOVER_GENERATION, response.voiceoverSecondsUsed));
  yield put(
    voiceoverGenerationSuccess({
      voiceoverUrl: response.url,
      voiceoverId: response.id,
      voiceoverAssetId: audioClipData.projectAssetId
    })
  );
}

function* generateVoiceoverSaga(): Generator<
  CallEffect | PutEffect | SelectEffect,
  void,
  VoiceoverGenerationApiData | VoiceoverGenerationProgressApiData | VoiceoversState
> {
  const {
    selectedRegion: selectedLocale,
    selectedVoice,
    scriptText: prompt,
    scriptName
  } = (yield select(state => state.voiceovers)) as VoiceoversState;

  if (!selectedVoice || !selectedLocale) {
    yield put(showError({ message: `Voice not found`, description: 'Voice not found' }));
    return;
  }

  const transformedVoiceData = {
    gender: selectedVoice.gender,
    displayedName: selectedVoice.name,
    originalName: selectedVoice.voice
  };

  try {
    const { executionId } = (yield call(
      { fn: callGenerateVoiceover, context: appServices },
      prompt,
      selectedLocale,
      transformedVoiceData
    )) as VoiceoverGenerationApiData;

    let isDone = false;

    while (!isDone) {
      const response = (yield call(callFetchVoiceoverProgress, executionId)) as VoiceoverGenerationProgressApiData;

      if (response.status === VoiceoverGenerationStatus.PENDING) {
        yield delay(1000);
        continue;
      }

      isDone = true;

      if (response.status === VoiceoverGenerationStatus.SUCCEEDED) {
        yield call(handleVoiceoverGenerationSuccess, response, prompt, scriptName);
        return;
      }

      const failedMessage =
        'There was an error with the server so we could not generate your voiceover. Please try again.';
      yield put(voiceoverGenerationFailed({ error: failedMessage }));
      yield put(showError({ message: `Failed to generate voiceover`, description: failedMessage }));
    }
  } catch (error) {
    if (error instanceof Error) {
      yield put(voiceoverGenerationFailed({ error: 'Failed to generate voiceover: ' + error.message }));
      yield put(showError({ message: `Failed to generate voiceover`, description: error.message }));
    }
  }
}

const formatScenes = (scenes: GeneratedScene[]) => {
  return scenes.map((scene: GeneratedScene) => `Scene ${scene.scene} \n\n ${scene.narration}`).join('\n\n');
};

function* generateScript(
  action: GenerateScriptAction
): Generator<
  PutEffect | CallEffect | SelectEffect,
  void,
  ScriptGenerationApiJobCreationData | ScriptGenerationInputs | ScriptGenerationApiStatusData
> {
  const projectId = action.projectId;
  const { prompt, lengthOfScriptMinutes, lengthOfScriptSeconds, predefinedStyles, customStyle } = (yield select(
    getScriptGenerationInputs
  )) as ScriptGenerationInputs;

  try {
    const lengthInSeconds = lengthOfScriptMinutes * 60 + lengthOfScriptSeconds;
    const styles = [
      ...predefinedStyles.filter(style => style.isChecked).map(style => style.label),
      ...(customStyle ? [customStyle] : [])
    ];
    const response = (yield call(
      appServices.generateScript,
      prompt,
      lengthInSeconds,
      styles
    )) as ScriptGenerationApiJobCreationData;

    const executionId = response.executionId;
    if (!executionId) {
      throw new Error('Failed to generate script: executionId is undefined');
    }

    let pollResponse: ScriptGenerationApiStatusData = {
      status: ScriptGenerationStatus.Pending
    };
    while (pollResponse.status === ScriptGenerationStatus.Pending) {
      pollResponse = (yield call(
        appServices.fetchScriptGenerationProgress,
        executionId
      )) as ScriptGenerationApiStatusData;

      if (pollResponse.status === ScriptGenerationStatus.Pending) {
        yield delay(5000);
        continue;
      }
    }

    if (pollResponse.status === ScriptGenerationStatus.Failed) {
      throw new Error(`Failed to generate script with executionId: ${executionId}`);
    }

    const script = formatScenes(pollResponse.script.scenes);

    yield put(updateEditingScript({ script: { projectId: projectId, projectScript: script } }));
    yield put(generateScriptSuccess(script));
  } catch (error) {
    if (error instanceof Error) {
      yield put(
        showError({ message: `There was an error generating the script, please try again`, description: error.message })
      );
      yield put(changeAiPanelStep(AiPanelTab.SCRIPT_GENERATION, AiScriptGenerationTabStep.OVERVIEW));
      yield put(generateScriptFailed(error));
    }
  }
}

function* saveGeneratedScript(action: SaveGeneratedScriptAction) {
  const script = {
    projectId: action.projectId,
    projectScript: action.script
  };
  yield put(setEditingScript({ script }));
  yield put(showLeftHandPanel(LeftHandPanel.SCRIPTS, undefined, ChangeStepTrigger.SCRIPT_GENERATION_TAB));
  yield put(updateEditingScript({ script })); // Trigger a sync
  yield take(syncSuccess.type); // Wait for the sync to complete before resetting the script generation state
  yield put(resetScriptGeneration());
}

function* tryNewScriptPrompt(action: TryNewScriptPromptAction) {
  if (action.reset) {
    yield put(resetScriptGeneration());
  }
  yield put(changeAiPanelStep(AiPanelTab.SCRIPT_GENERATION, AiScriptGenerationTabStep.OVERVIEW));
}

export default function* aiFeaturesSagas() {
  yield takeEvery(GENERATE_IMAGES, generateImages);
  yield takeEvery(REGENERATE_IMAGES, regenerateImages);
  yield takeEvery(ADD_GENERATED_IMAGE_TO_PROJECT, addGeneratedImageToProject);
  yield takeEvery(TRY_NEW_IMAGE_PROMPT, tryNewImagePrompt);
  yield takeEvery(generateVoiceover.type, generateVoiceoverSaga);
  yield takeEvery(GENERATE_SCRIPT, generateScript);
  yield takeEvery(SAVE_GENERATED_SCRIPT, saveGeneratedScript);
  yield takeEvery(TRY_NEW_SCRIPT_PROMPT, tryNewScriptPrompt);
}
